// ═══════════════════════════════════════════════════════════════
// VaultZkSettingsPanel.js — Section Paramètres → Mode renforcé V2
// ═══════════════════════════════════════════════════════════════
//
// Pilotage complet V2 zero-knowledge depuis l'onglet Paramètres
// du coffre-fort :
//   - Activer (wizard 3 étapes)
//   - Changer la passphrase (rotate inline)
//   - Activer / désactiver 2FA TOTP
//   - Migrer items V1 → V2 (mode transit ou information sur strict)
//   - Désactiver le mode renforcé (refuse si items V2 vivants)
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useState, useCallback } from 'react';
import {
    Shield, ShieldCheck, ShieldOff, KeyRound, Lock, Unlock,
    Smartphone, ArrowRightLeft, Loader2, AlertTriangle, Info,
} from 'lucide-react';
import { useVaultZk } from './VaultZkContext';
import { useTheme } from '../../contexts/ThemeContext';
import { toast, confirm as confirmModal } from '../ui/Toast';
import {
    deactivateZk, rotateZk, totpDisable, migrateItem,
} from '../../services/vaultZkApi';
import {
    deriveKEKFromPassphrase, unwrapDEK, verifyTestBlob, encryptItem,
    randomSaltBase64, wrapDEK, createTestBlob, VAULT_ZK_KDF_LABEL, VAULT_ZK_DEFAULT_ITER,
} from '../../utils/vaultZkCrypto';
import { listVaultItems, readVaultItem } from '../../services/vaultApi';
import VaultZkActivationWizard from './VaultZkActivationWizard';
import VaultZkUnlockModal from './VaultZkUnlockModal';
import VaultZkTotpSetup from './VaultZkTotpSetup';

const styles = {
    section: (isDark) => ({
        background: isDark ? '#13131a' : '#ffffff',
        border: isDark ? '1px solid #2c2c3c' : '1px solid #e3e3ee',
        borderRadius: 12, padding: 20, marginBottom: 16,
    }),
    head: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 },
    h: { fontSize: 15, fontWeight: 600, margin: 0 },
    sub: (isDark) => ({ fontSize: 12.5, color: isDark ? '#9090a8' : '#6a6a85', margin: '0 0 16px', lineHeight: 1.55 }),
    row: { display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 },
    btn: (variant = 'primary', isDark = false) => {
        const base = {
            padding: '9px 14px', borderRadius: 9, fontSize: 13.5,
            cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7,
            border: 'none', fontWeight: 500,
        };
        if (variant === 'primary') return {
            ...base,
            background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
            color: '#fff', boxShadow: '0 4px 12px rgba(109, 40, 217, 0.28)',
        };
        if (variant === 'danger') return {
            ...base, background: 'transparent', color: '#ef4444',
            border: '1px solid rgba(239, 68, 68, 0.3)',
        };
        if (variant === 'ghost') return {
            ...base, background: 'transparent',
            color: isDark ? '#b8b8c8' : '#5a5a72',
            border: isDark ? '1px solid #2c2c3c' : '1px solid #d8d8e2',
        };
        return base;
    },
    statusBadge: (active) => ({
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '3px 10px', borderRadius: 99,
        background: active ? 'rgba(34, 197, 94, 0.12)' : 'rgba(148, 148, 168, 0.12)',
        color: active ? '#22c55e' : '#9090a8',
        fontSize: 11.5, fontWeight: 500, marginLeft: 8,
    }),
    info: {
        fontSize: 12.5, lineHeight: 1.5, padding: 12, borderRadius: 9,
        background: 'rgba(59, 130, 246, 0.07)',
        border: '1px solid rgba(59, 130, 246, 0.18)',
        color: '#3b82f6', display: 'flex', gap: 9, alignItems: 'flex-start',
    },
    warn: {
        fontSize: 12.5, lineHeight: 1.5, padding: 12, borderRadius: 9,
        background: 'rgba(245, 158, 11, 0.08)',
        border: '1px solid rgba(245, 158, 11, 0.25)',
        color: '#f59e0b', display: 'flex', gap: 9, alignItems: 'flex-start',
    },
    inputBox: (isDark) => ({
        width: '100%', padding: '9px 11px', fontSize: 13,
        background: isDark ? '#1c1c28' : '#f7f7fb',
        color: isDark ? '#e8e8f0' : '#1a1a2a',
        border: isDark ? '1px solid #2c2c3c' : '1px solid #d8d8e2',
        borderRadius: 7, outline: 'none', boxSizing: 'border-box',
    }),
    progress: { height: 6, background: 'rgba(139,92,246,0.15)', borderRadius: 99, overflow: 'hidden', marginTop: 10 },
    progressBar: (pct) => ({ height: '100%', width: `${pct}%`, background: '#8b5cf6', transition: 'width 200ms' }),
};

export default function VaultZkSettingsPanel({ accountLabel }) {
    const { isDark } = useTheme();
    const { challenge, refreshChallenge, isUnlocked, dek, lock } = useVaultZk();

    const [showActivate, setShowActivate] = useState(false);
    const [showUnlock, setShowUnlock] = useState(false);
    const [showTotp, setShowTotp] = useState(false);
    const [showRotate, setShowRotate] = useState(false);
    const [showMigrate, setShowMigrate] = useState(false);
    const [v1Count, setV1Count] = useState(null); // null = pas encore loadé

    const refreshV1Count = useCallback(async () => {
        try {
            const r = await listVaultItems();
            const list = r.items || [];
            setV1Count(list.filter((it) => !it.vaultZkEnabled).length);
        } catch (e) {
            setV1Count(0);
        }
    }, []);

    useEffect(() => {
        refreshChallenge();
        refreshV1Count();
    }, [refreshChallenge, refreshV1Count]);

    const zkOn = !!challenge?.vault_zk_enabled;
    const totpOn = !!challenge?.totp_enabled;

    async function onDeactivate() {
        const ok = await confirmModal(
            'Désactiver le mode renforcé ? Vos items V2 doivent être supprimés ou exportés au préalable. Le mode standard (V1) reprendra ensuite.',
            { dangerLabel: 'Désactiver', cancelLabel: 'Annuler' }
        );
        if (!ok) return;
        try {
            await deactivateZk();
            await refreshChallenge();
            lock();
            toast.success('Mode renforcé désactivé');
        } catch (e) {
            if (e.code === 'items_v2_remaining') {
                toast.error(`${e.detail?.count || 'Plusieurs'} item(s) V2 actif(s) — supprimez-les ou exportez-les avant.`);
            } else {
                toast.error(`Désactivation refusée : ${e.message}`);
            }
        }
    }

    async function onTotpDisable() {
        const ok = await confirmModal(
            'Désactiver le 2FA ? Le déverrouillage ne demandera plus que la passphrase.',
            { dangerLabel: 'Désactiver 2FA' }
        );
        if (!ok) return;
        try {
            await totpDisable();
            await refreshChallenge();
            toast.success('2FA désactivé');
        } catch (e) {
            toast.error(`Erreur : ${e.message}`);
        }
    }

    return (
        <div>
            {/* ─── Section Mode renforcé ──────────────────────────── */}
            <div style={styles.section(isDark)}>
                <div style={styles.head}>
                    <Shield size={18} color="#8b5cf6" />
                    <h3 style={styles.h}>Mode renforcé (zero-knowledge)</h3>
                    <span style={styles.statusBadge(zkOn)}>
                        {zkOn ? <ShieldCheck size={11} /> : <ShieldOff size={11} />}
                        {zkOn ? 'Actif' : 'Inactif'}
                    </span>
                </div>
                <p style={styles.sub(isDark)}>
                    {zkOn
                        ? 'Vos items V2 sont chiffrés par une clé dérivée de votre passphrase, jamais stockée sur le serveur. Même un administrateur root ne peut pas les déchiffrer.'
                        : 'Activez le mode renforcé pour qu\'aucune lecture serveur ne soit possible — y compris par l\'administrateur. Vous serez le seul détenteur de la clé.'}
                </p>

                {!zkOn && (
                    <button style={styles.btn('primary')} onClick={() => setShowActivate(true)}>
                        <Lock size={15} />
                        Activer le mode renforcé
                    </button>
                )}

                {zkOn && (
                    <>
                        <div style={styles.info}>
                            <Info size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                            <span>Activé le {challenge?.activated_at ? new Date(challenge.activated_at).toLocaleDateString('fr-FR') : '—'}.
                                {!isUnlocked && ' Le coffre est verrouillé dans cet onglet.'}</span>
                        </div>

                        <div style={styles.row}>
                            {!isUnlocked && (
                                <button style={styles.btn('primary')} onClick={() => setShowUnlock(true)}>
                                    <Unlock size={14} />
                                    Déverrouiller maintenant
                                </button>
                            )}
                            {isUnlocked && (
                                <button style={styles.btn('ghost', isDark)} onClick={lock}>
                                    <Lock size={14} />
                                    Verrouiller cet onglet
                                </button>
                            )}
                            <button
                                style={styles.btn('ghost', isDark)}
                                onClick={() => isUnlocked ? setShowRotate(true) : toast.info('Déverrouillez d\'abord pour changer la passphrase.')}
                            >
                                <KeyRound size={14} />
                                Changer la passphrase
                            </button>
                            {v1Count !== 0 && (
                                <button
                                    style={styles.btn('ghost', isDark)}
                                    onClick={() => setShowMigrate(true)}
                                    disabled={!isUnlocked || v1Count === null}
                                    title={!isUnlocked ? 'Déverrouillez d\'abord' : null}
                                >
                                    <ArrowRightLeft size={14} />
                                    Migrer items V1 → V2{v1Count > 0 ? ` (${v1Count})` : ''}
                                </button>
                            )}
                            <button style={styles.btn('danger')} onClick={onDeactivate}>
                                <ShieldOff size={14} />
                                Désactiver le mode renforcé
                            </button>
                        </div>
                    </>
                )}
            </div>

            {/* ─── Section 2FA TOTP (conditionnelle) ───────────────── */}
            {zkOn && (
                <div style={styles.section(isDark)}>
                    <div style={styles.head}>
                        <Smartphone size={18} color="#8b5cf6" />
                        <h3 style={styles.h}>Authentification à deux facteurs</h3>
                        <span style={styles.statusBadge(totpOn)}>
                            {totpOn ? <ShieldCheck size={11} /> : <ShieldOff size={11} />}
                            {totpOn ? 'Activée' : 'Inactive'}
                        </span>
                    </div>
                    <p style={styles.sub(isDark)}>
                        Code 6 chiffres généré par votre app authenticator (Google Authenticator, 1Password, Bitwarden…).
                        Demandé à chaque déverrouillage en plus de la passphrase.
                    </p>

                    {!totpOn ? (
                        <button
                            style={styles.btn('primary')}
                            onClick={() => isUnlocked ? setShowTotp(true) : toast.info('Déverrouillez d\'abord pour activer la 2FA.')}
                        >
                            <Smartphone size={14} />
                            Activer la 2FA
                        </button>
                    ) : (
                        <button style={styles.btn('danger')} onClick={onTotpDisable}>
                            <ShieldOff size={14} />
                            Désactiver la 2FA
                        </button>
                    )}
                </div>
            )}

            {/* ─── Modales ────────────────────────────────────────── */}
            {showActivate && (
                <VaultZkActivationWizard
                    onClose={() => setShowActivate(false)}
                    onActivated={() => refreshChallenge()}
                />
            )}
            {showUnlock && challenge?.vault_zk_enabled && (
                <VaultZkUnlockModal
                    challenge={challenge}
                    onCancel={() => setShowUnlock(false)}
                />
            )}
            {showTotp && challenge && (
                <VaultZkTotpSetup
                    challenge={challenge}
                    accountLabel={accountLabel}
                    onClose={() => setShowTotp(false)}
                />
            )}
            {showRotate && isUnlocked && (
                <RotatePassphraseModal
                    challenge={challenge}
                    dek={dek}
                    onClose={() => setShowRotate(false)}
                    onSuccess={refreshChallenge}
                />
            )}
            {showMigrate && isUnlocked && (
                <MigrateItemsModal
                    challenge={challenge}
                    dek={dek}
                    onClose={() => { setShowMigrate(false); refreshV1Count(); }}
                />
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// RotatePassphraseModal — change la passphrase, DEK inchangé
// ─────────────────────────────────────────────────────────────

function RotatePassphraseModal({ challenge, dek, onClose, onSuccess }) {
    const { isDark } = useTheme();
    const [oldPwd, setOldPwd] = useState('');
    const [newPwd1, setNewPwd1] = useState('');
    const [newPwd2, setNewPwd2] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    async function rotate() {
        setErr('');
        if (newPwd1.length < 12) { setErr('Nouvelle passphrase : 12 caractères minimum.'); return; }
        if (newPwd1 !== newPwd2) { setErr('Les nouvelles passphrases ne correspondent pas.'); return; }
        setBusy(true);
        try {
            // Vérifier l'ancienne via test_blob (meilleur que de re-déwrap)
            const oldKek = await deriveKEKFromPassphrase(oldPwd, challenge.salt, challenge.iter);
            const ok = await verifyTestBlob(challenge.test_blob, oldKek);
            if (!ok) { setErr('Ancienne passphrase incorrecte.'); return; }

            // Re-wrap le DEK déjà en mémoire
            const newSalt = randomSaltBase64(32);
            const newKek = await deriveKEKFromPassphrase(newPwd1, newSalt, VAULT_ZK_DEFAULT_ITER);
            const newWrapped = await wrapDEK(dek, newKek);
            const newTest = await createTestBlob(newKek);

            await rotateZk({
                kdf: VAULT_ZK_KDF_LABEL,
                iter: VAULT_ZK_DEFAULT_ITER,
                salt: newSalt,
                wrapped_dek: newWrapped,
                test_blob: newTest,
            });
            toast.success('Passphrase changée');
            if (onSuccess) await onSuccess();
            if (onClose) onClose();
        } catch (e) {
            setErr(`Échec : ${e.message}`);
        } finally {
            setBusy(false);
        }
    }

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(8,8,18,0.78)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
            <div style={{
                width: 'min(440px,100%)', background: isDark ? '#13131a' : '#ffffff',
                color: isDark ? '#e8e8f0' : '#1a1a2a',
                borderRadius: 16, padding: '26px 30px',
                border: isDark ? '1px solid rgba(139,92,246,0.2)' : '1px solid rgba(139,92,246,0.18)',
                boxShadow: '0 24px 48px rgba(76,29,149,0.35)',
            }}>
                <h2 style={{ fontSize: 17, fontWeight: 600, margin: '0 0 6px' }}>
                    <KeyRound size={18} color="#8b5cf6" style={{ verticalAlign: -3, marginRight: 8 }} />
                    Changer la passphrase
                </h2>
                <p style={{ fontSize: 13, color: isDark ? '#9090a8' : '#6a6a85', margin: '0 0 18px' }}>
                    Le mnémonique BIP39 reste valide. La récupération continue de fonctionner.
                </p>

                <label style={styles.sub(isDark)}>Ancienne passphrase</label>
                <input type="password" value={oldPwd} onChange={(e) => setOldPwd(e.target.value)}
                    style={{ ...styles.inputBox(isDark), marginBottom: 10 }} autoFocus />

                <label style={styles.sub(isDark)}>Nouvelle passphrase</label>
                <input type="password" value={newPwd1} onChange={(e) => setNewPwd1(e.target.value)}
                    style={{ ...styles.inputBox(isDark), marginBottom: 10 }} />

                <label style={styles.sub(isDark)}>Confirmer la nouvelle passphrase</label>
                <input type="password" value={newPwd2} onChange={(e) => setNewPwd2(e.target.value)}
                    style={{ ...styles.inputBox(isDark), marginBottom: 14 }} />

                {err && <div style={{ color: '#ef4444', fontSize: 12.5, marginBottom: 10 }}>{err}</div>}

                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button style={styles.btn('ghost', isDark)} onClick={onClose}>Annuler</button>
                    <button style={styles.btn('primary')} onClick={rotate} disabled={busy}>
                        {busy ? <Loader2 size={14} className="spin" /> : <KeyRound size={14} />}
                        Changer
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// MigrateItemsModal — migration progressive V1 → V2
// ─────────────────────────────────────────────────────────────
// Mode 'transit' : boucle automatisée (déchiffrement serveur transitoire)
// Mode 'strict' : info textuelle — l'user doit re-saisir manuellement

function MigrateItemsModal({ challenge, dek, onClose }) {
    const { isDark } = useTheme();
    const [items, setItems] = useState(null);
    const [v1Items, setV1Items] = useState([]);
    const [mode, setMode] = useState('transit');
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState({ done: 0, total: 0, errors: 0 });
    const [err, setErr] = useState('');

    useEffect(() => {
        listVaultItems()
            .then((r) => {
                const list = r.items || [];
                setItems(list);
                setV1Items(list.filter((it) => !it.vaultZkEnabled));
            })
            .catch((e) => setErr(e.message));
    }, []);

    async function migrateAllTransit() {
        setBusy(true); setErr('');
        const total = v1Items.length;
        setProgress({ done: 0, total, errors: 0 });
        let done = 0; let errors = 0;
        for (const it of v1Items) {
            try {
                const v1 = await readVaultItem(it.id);
                if (!v1.content) throw new Error('contenu vide reçu');
                // Re-chiffrer avec DEK V2
                const envelope = await encryptItem(v1.content, dek);
                // Format envelope = "b64(iv).b64(ct+tag)"
                const dot = envelope.indexOf('.');
                const iv = envelope.slice(0, dot);
                const ciphertext = envelope.slice(dot + 1);
                await migrateItem(it.id, { iv, ciphertext, mode: 'transit' });
                done += 1;
            } catch (e) {
                errors += 1;
                // eslint-disable-next-line no-console
                console.warn('migrate item failed', it.id, e);
            } finally {
                setProgress({ done, total, errors });
            }
        }
        setBusy(false);
        if (errors === 0) {
            toast.success(`${done} item(s) migré(s) vers le mode renforcé`);
            // Auto-close après succès complet — le parent (onClose) refreshV1Count()
            // pour cacher le bouton "Migrer items V1 → V2" devenu inutile.
            if (typeof onClose === 'function') {
                setTimeout(() => onClose(), 600);
            }
        } else {
            toast.warning(`${done} migré(s), ${errors} échec(s) — vérifiez la console`);
        }
    }

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(8,8,18,0.78)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
            <div style={{
                width: 'min(540px,100%)', background: isDark ? '#13131a' : '#ffffff',
                color: isDark ? '#e8e8f0' : '#1a1a2a',
                borderRadius: 16, padding: '26px 30px',
                border: isDark ? '1px solid rgba(139,92,246,0.2)' : '1px solid rgba(139,92,246,0.18)',
                boxShadow: '0 24px 48px rgba(76,29,149,0.35)',
            }}>
                <h2 style={{ fontSize: 17, fontWeight: 600, margin: '0 0 6px' }}>
                    <ArrowRightLeft size={18} color="#8b5cf6" style={{ verticalAlign: -3, marginRight: 8 }} />
                    Migration V1 → V2
                </h2>
                <p style={{ fontSize: 13, color: isDark ? '#9090a8' : '#6a6a85', margin: '0 0 18px' }}>
                    {items === null
                        ? 'Chargement de vos items…'
                        : v1Items.length === 0
                            ? 'Aucun item V1 à migrer — tout est déjà en mode renforcé.'
                            : `${v1Items.length} item(s) V1 disponible(s) à migrer en mode renforcé.`}
                </p>

                {v1Items.length > 0 && (
                    <>
                        <div style={{ marginBottom: 14 }}>
                            <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', marginBottom: 8, fontSize: 13, lineHeight: 1.5 }}>
                                <input type="radio" name="mig" checked={mode === 'transit'} onChange={() => setMode('transit')} style={{ marginTop: 3 }} />
                                <span>
                                    <strong>Mode transit (recommandé)</strong> — boucle automatisée. Pour chaque item, le serveur déchiffre V1 le temps de le renvoyer au navigateur, qui le re-chiffre en V2. Quelques secondes par item, plaintext en transit.
                                </span>
                            </label>
                            <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: 13, lineHeight: 1.5 }}>
                                <input type="radio" name="mig" checked={mode === 'strict'} onChange={() => setMode('strict')} style={{ marginTop: 3 }} />
                                <span>
                                    <strong>Mode strict (100% zero-knowledge)</strong> — aucune lecture serveur. Vous re-saisissez chaque item manuellement (supprimer V1 → recréer V2). Lourd mais zéro fenêtre transit.
                                </span>
                            </label>
                        </div>

                        {mode === 'strict' && (
                            <div style={styles.warn}>
                                <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                                <span>Mode strict : pas d'automation côté UI. Allez dans l'onglet Actifs, ouvrez chaque item, copiez son contenu, supprimez-le puis re-créez-le depuis le chat — il sera alors V2 directement.</span>
                            </div>
                        )}

                        {mode === 'transit' && progress.total > 0 && (
                            <div>
                                <div style={{ fontSize: 12.5, marginBottom: 6, color: isDark ? '#9090a8' : '#6a6a85' }}>
                                    {progress.done}/{progress.total} migré(s){progress.errors > 0 ? ` · ${progress.errors} échec(s)` : ''}
                                </div>
                                <div style={styles.progress}>
                                    <div style={styles.progressBar(progress.total ? (progress.done / progress.total) * 100 : 0)} />
                                </div>
                            </div>
                        )}

                        {err && <div style={{ color: '#ef4444', fontSize: 12.5, marginTop: 10 }}>{err}</div>}

                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
                            <button style={styles.btn('ghost', isDark)} onClick={onClose}>{busy ? 'Migration en cours…' : 'Fermer'}</button>
                            {mode === 'transit' && (
                                <button style={styles.btn('primary')} onClick={migrateAllTransit} disabled={busy}>
                                    {busy ? <Loader2 size={14} className="spin" /> : <ArrowRightLeft size={14} />}
                                    Lancer la migration ({v1Items.length})
                                </button>
                            )}
                        </div>
                    </>
                )}

                {(items !== null && v1Items.length === 0) && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
                        <button style={styles.btn('ghost', isDark)} onClick={onClose}>Fermer</button>
                    </div>
                )}
            </div>
        </div>
    );
}
